/**
 * CreatorProfileDrawer — cross-product creator detail surface.
 *
 * SLOT IN THE FUSION ARCHITECTURE
 * --------------------------------
 * When a brand clicks a creator avatar anywhere in DSP (Wave Control
 * Room, Discover, Patches roster), they need creator-level depth that
 * lives PRIMARILY on the SSP side: full content history, audience
 * analytics, KYC status, payout history.
 *
 * This drawer is the DSP-side host. Today (Wave 3 prep) it renders
 * with local DSP data only. Once Wave 3 launches:
 *   • An `<iframe src={`${SSP_BASE}/embed/creator/{email}`} />` will
 *     slot in below the local summary and render the SSP-side detail.
 *   • A postMessage contract synchronizes scroll + close events.
 *
 * This file is the local-data renderer + the slot for the SSP embed.
 * The embed is feature-flagged off until SSP ships their embed page +
 * we agree on the iframe origin allowlist.
 */
import React from 'react';
import { X, ExternalLink, Mail, Sparkles, ShieldCheck } from 'lucide-react';
import { COLORS, RADIUS, Z } from '../../lib/design-tokens';

const SSP_EMBED_ENABLED = false;     // Wave 3 step 2 — flip when SSP ships /embed/creator/{email}

export default function CreatorProfileDrawer({
  open,
  onClose,
  creator,
  context,       // { wave_id, brand_id, source } — passed to the SSP embed
}) {
  if (!open || !creator) return null;

  const handle = creator.handle || creator.creator_handle || creator.email || 'creator';
  const email  = creator.email || creator.creator_email;
  const followers = creator.followers ?? creator.audience_size;
  const tier   = creator.tier || creator.audience_tier;
  const kyc    = creator.kyc_status || creator.payout_eligibility;

  return (
    <div
      className="fixed inset-0 flex items-stretch justify-end"
      style={{ background: 'rgba(10,10,10,0.78)', zIndex: Z.drawer }}
      data-testid="creator-profile-drawer-backdrop"
      onClick={onClose}
    >
      <aside
        className="w-full sm:w-[480px] h-full overflow-y-auto flex flex-col"
        style={{
          background: COLORS.surface.panelRaised,
          borderLeft: `1px solid ${COLORS.surface.border}`,
        }}
        data-testid="creator-profile-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <header
          className="sticky top-0 px-6 py-4 flex items-start justify-between gap-3"
          style={{
            background: COLORS.surface.panelRaised,
            borderBottom: `1px solid ${COLORS.surface.border}`,
            zIndex: Z.sticky,
          }}
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-black"
               style={{ color: COLORS.text.tertiary }}>
              Creator profile
            </p>
            <p className="text-2xl font-black mt-1 truncate"
               style={{ color: COLORS.text.primary }}>
              @{handle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="creator-profile-drawer-close"
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" style={{ color: COLORS.text.tertiary }} />
          </button>
        </header>

        {/* ─── Local-data summary (DSP-owned) ─── */}
        <section className="px-6 py-5 space-y-3"
                 data-testid="creator-profile-local-summary">
          <SummaryRow
            label="Followers"
            value={followers ? formatCompact(followers) : '—'}
          />
          {tier && <SummaryRow label="Tier" value={tier} />}
          {email && (
            <SummaryRow
              label="Email"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-3 w-3" style={{ color: COLORS.text.tertiary }} />
                  <span style={{ color: COLORS.text.secondary }}>{email}</span>
                </span>
              }
            />
          )}
          {kyc && (
            <SummaryRow
              label="Payout eligibility"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck
                    className="h-3 w-3"
                    style={{
                      color: kyc === 'verified' || kyc === 'cleared'
                        ? COLORS.status.success
                        : COLORS.status.warning,
                    }}
                  />
                  <span style={{
                    color: kyc === 'verified' || kyc === 'cleared'
                      ? COLORS.status.success
                      : COLORS.status.warning,
                  }}>
                    {kyc === 'verified' || kyc === 'cleared' ? 'KYC complete' : 'KYC pending'}
                  </span>
                </span>
              }
            />
          )}
        </section>

        {/* ─── CMD context (when opened from a patch) ─── */}
        {context?.patch_id && (
          <section
            className="mx-6 mb-4 px-3 py-2.5 rounded-lg flex items-center gap-2"
            style={{
              background: COLORS.brand.cmdSoft,
              border: `1px solid ${COLORS.brand.cmdBorder}`,
            }}
            data-testid="creator-profile-patch-context"
          >
            <Sparkles className="h-3.5 w-3.5 flex-shrink-0"
                      style={{ color: COLORS.brand.cmd }} />
            <p className="text-[11.5px]" style={{ color: COLORS.text.secondary }}>
              On Patch {context.patch_id} · {context.patch_role || 'team member'} in this wave.
            </p>
          </section>
        )}

        {/* ─── SSP embed slot (Wave 3 feature-flagged) ─── */}
        {SSP_EMBED_ENABLED && email ? (
          <section
            className="flex-1 px-6 pb-6"
            data-testid="creator-profile-ssp-embed"
          >
            <iframe
              title="Creator detail (SSP)"
              src={`${process.env.REACT_APP_SSP_BASE_URL}/embed/creator/${encodeURIComponent(email)}?dsp_context=${encodeURIComponent(JSON.stringify(context || {}))}`}
              className="w-full h-full min-h-[400px]"
              style={{
                background: COLORS.surface.panel,
                border: `1px solid ${COLORS.surface.border}`,
                borderRadius: RADIUS.md,
              }}
              sandbox="allow-scripts allow-same-origin"
            />
          </section>
        ) : (
          <section
            className="mx-6 mb-6 px-4 py-6 rounded-lg text-center"
            style={{
              background: COLORS.surface.panel,
              border: `1px dashed ${COLORS.surface.border}`,
            }}
            data-testid="creator-profile-ssp-embed-placeholder"
          >
            <p className="text-[11px] uppercase tracking-[0.18em] font-black mb-1.5"
               style={{ color: COLORS.text.tertiary }}>
              Full profile on SSP
            </p>
            <p className="text-[12.5px] mb-3" style={{ color: COLORS.text.secondary }}>
              Content history, audience analytics, payout records, and brief inbox
              live on the creator side of the platform.
            </p>
            <a
              href={email ? `https://wavedsp.ai/creator/${encodeURIComponent(email)}` : '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition-colors"
              style={{ color: COLORS.brand.primary }}
              data-testid="creator-profile-ssp-link"
            >
              <ExternalLink className="h-3 w-3" />
              Open full profile
            </a>
          </section>
        )}
      </aside>
    </div>
  );
}

// ─── Internal helpers ──────────────────────────────────────────────────

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5"
         style={{ borderBottom: `1px solid ${COLORS.surface.borderSoft}` }}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-black flex-shrink-0"
         style={{ color: COLORS.text.tertiary }}>
        {label}
      </p>
      <div className="text-[12.5px] font-bold text-right min-w-0"
           style={{ color: COLORS.text.primary }}>
        {value}
      </div>
    </div>
  );
}

function formatCompact(n) {
  if (typeof n !== 'number') return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}
